/**
 * Converts AI message text to safe, renderable HTML.
 *
 * The server runs the same transforms in openai.js before saving the
 * final message. However, streaming tokens arrive as raw markdown.
 * This function handles BOTH cases safely:
 *
 * 1. Already-HTML input (completed messages): preserves sanitized formatting.
 * 2. Raw markdown (streaming tokens): converts the same subset the server uses:
 *      **bold**     → <strong>bold</strong>
 *      *italic*     → <em>italic</em>
 *      [label](url) → a safe external or reservation link
 *      bare URLs    → <a href="url" ...>url</a>
 *
 * Security: DOMPurify sanitizes both input paths with a formatting allowlist.
 *
 * Usage:
 *   import { renderAIHtml } from '../common/renderAIHtml';
 *   <div dangerouslySetInnerHTML={{ __html: renderAIHtml(text) }} />
 */

import DOMPurify from "dompurify";

const URL_RE = /(\bhttps?:\/\/[^\s<>"']+|\bwww\.[^\s<>"']+)/g;
const TRAILING_PUNCT = /[.,;:!?)\]]+$/;
const FORMATTING_TAGS = [
  "strong", "b", "em", "i", "a", "br", "p", "ul", "ol", "li",
  "blockquote", "code", "pre", "h1", "h2", "h3", "h4", "h5", "h6",
];
const SANITIZE_OPTIONS = {
  ALLOWED_TAGS: FORMATTING_TAGS,
  ALLOWED_ATTR: ["href", "target", "rel", "title"],
  ALLOW_DATA_ATTR: false,
  ALLOW_ARIA_ATTR: false,
};

export function renderAIHtml(text) {
  if (!text) return "";

  const isAlreadyHtml = new RegExp(
    `<(?:${FORMATTING_TAGS.join("|")})\\b`,
    "i",
  ).test(text);
  let html = text;
  if (!isAlreadyHtml) {
    html = text
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;")
      .replace(/\[([^\]]+)\]\(\/?(guests\/[a-f0-9]+)\)/gi, '<a href="/$2">$1</a>')
      .replace(
        /\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g,
        '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>',
      )
      .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
      .replace(/(?<!\*)\*(?!\*)(.*?)(?<!\*)\*(?!\*)/g, "<em>$1</em>");
  }

  const container = document.createElement("div");
  container.innerHTML = DOMPurify.sanitize(html, SANITIZE_OPTIONS);
  const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
  const textNodes = [];
  while (walker.nextNode()) {
    if (!walker.currentNode.parentElement.closest("a, code, pre")) {
      textNodes.push(walker.currentNode);
    }
  }

  for (const textNode of textNodes) {
    const fragment = document.createDocumentFragment();
    let offset = 0;
    for (const match of textNode.textContent.matchAll(URL_RE)) {
      fragment.append(textNode.textContent.slice(offset, match.index));
      const trailing = (match[0].match(TRAILING_PUNCT) || [""])[0];
      const url = trailing ? match[0].slice(0, -trailing.length) : match[0];
      const anchor = document.createElement("a");
      anchor.setAttribute("href", url.startsWith("http") ? url : `https://${url}`);
      anchor.textContent = url;
      fragment.append(anchor, trailing);
      offset = match.index + match[0].length;
    }
    fragment.append(textNode.textContent.slice(offset));
    textNode.replaceWith(fragment);
  }

  for (const anchor of container.querySelectorAll("a[href]")) {
    if (/^(?:https?:)?\/\//i.test(anchor.getAttribute("href"))) {
      anchor.setAttribute("target", "_blank");
      anchor.setAttribute("rel", "noopener noreferrer");
    } else if (anchor.getAttribute("target") === "_blank") {
      anchor.setAttribute("rel", "noopener noreferrer");
    }
  }

  return DOMPurify.sanitize(container.innerHTML, SANITIZE_OPTIONS);
}
